import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { toast } from "sonner";
import { 
  BookOpen, 
  Plus, 
  Download, 
  Trash2, 
  Loader2, 
  FileText,
  ImageIcon,
  Smartphone,
  Tablet,
  Upload,
  ArrowRight,
  ArrowLeft,
  Check,
  X
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EbookCreator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ebooks, setEbooks] = useState([]);
  const [books, setBooks] = useState([]);
  const [covers, setCovers] = useState([]);
  const [userCredits, setUserCredits] = useState(0);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [sourceType, setSourceType] = useState("dtr_book");
  const [selectedBook, setSelectedBook] = useState("");
  const [externalContent, setExternalContent] = useState("");
  const [externalTitle, setExternalTitle] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [finalTitle, setFinalTitle] = useState("");  // Editable final title
  
  // Cover state
  const [coverSourceType, setCoverSourceType] = useState("none");
  const [selectedCover, setSelectedCover] = useState("none");
  const [uploadedCoverData, setUploadedCoverData] = useState(null);
  const [uploadedCoverName, setUploadedCoverName] = useState("");

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ebooksRes, booksRes, coversRes, userRes] = await Promise.all([
        axios.get(`${API}/ebooks`),
        axios.get(`${API}/books`),
        axios.get(`${API}/covers`),
        axios.get(`${API}/auth/me`)
      ]);
      
      setEbooks(ebooksRes.data.ebooks || []);
      const booksData = Array.isArray(booksRes.data) ? booksRes.data : (booksRes.data.books || []);
      setBooks(booksData.filter(b => b.status === "completed"));
      const coversData = Array.isArray(coversRes.data) ? coversRes.data : (coversRes.data.projects || []);
      setCovers(coversData.filter(c => c.covers?.length > 0 && c.status === "completed"));
      setUserCredits(userRes.data.credits || 0);
    } catch (error) {
      console.error("Failed to load data:", error);
      // If authentication failed, redirect to login
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      toast.error("Failed to load data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setSourceType("dtr_book");
    setSelectedBook("");
    setExternalContent("");
    setExternalTitle("");
    setUploadedFileName("");
    setFinalTitle("");
    setCoverSourceType("none");
    setSelectedCover("none");
    setUploadedCoverData(null);
    setUploadedCoverName("");
  };

  const handleBookFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['.txt', '.docx', '.pdf'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error("Please upload a .txt, .docx, or .pdf file");
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be less than 20MB");
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/extract-book-text`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for large files
      });
      
      if (response.data.text) {
        setExternalContent(response.data.text);
        setUploadedFileName(file.name);
        // Auto-set title from filename if not already set
        if (!externalTitle) {
          const titleFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
          setExternalTitle(titleFromFile);
        }
        toast.success(`Extracted ${response.data.text.length.toLocaleString()} characters from ${file.name}`);
      } else {
        toast.error("Could not extract text from file");
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract text from file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result?.toString().split(',')[1];
      setUploadedCoverData(base64);
      setUploadedCoverName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (sourceType === "dtr_book" && !selectedBook) {
      toast.error("Please select a book");
      return;
    }
    
    if (sourceType === "external" && (!externalContent.trim() || !externalTitle.trim())) {
      toast.error("Please enter a title and content for your book");
      return;
    }

    if (userCredits < 2) {
      toast.error("Insufficient credits. You need 2 credits to create an ebook.");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/ebooks/create`, {
        source_type: sourceType,
        book_id: sourceType === "dtr_book" ? selectedBook : null,
        external_content: sourceType === "external" ? externalContent : null,
        external_title: sourceType === "external" ? externalTitle : null,
        cover_id: coverSourceType === "dtr_cover" && selectedCover !== "none" ? selectedCover : null,
        external_cover_data: coverSourceType === "external" ? uploadedCoverData : null,
        format: "epub",
        include_toc: true
      });
      
      toast.success("Ebook created successfully!");
      setShowNewDialog(false);
      resetWizard();
      loadData();
    } catch (error) {
      console.error("Failed to create ebook:", error);
      toast.error(error.response?.data?.detail || "Failed to create ebook");
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (ebook) => {
    try {
      toast.loading("Preparing download...", { id: "download" });
      const response = await axios.get(`${API}/ebooks/${ebook.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${ebook.title}.epub`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!", { id: "download" });
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download ebook", { id: "download" });
    }
  };

  const handleDelete = async (ebookId) => {
    if (!confirm("Are you sure you want to delete this ebook?")) return;
    
    try {
      await axios.delete(`${API}/ebooks/${ebookId}`);
      toast.success("Ebook deleted");
      loadData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete ebook");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const canProceedStep1 = sourceType !== "";
  const canProceedStep2 = sourceType === "dtr_book" ? selectedBook !== "" : (externalContent.trim() !== "" && externalTitle.trim() !== "");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Ebook Formatter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-stone-50 to-neutral-100">
      <Sidebar onLogout={handleLogout} />
      
      <div className="ml-0 md:ml-64 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold playfair text-stone-900">Ebook Formatter</h1>
              <p className="text-stone-600 mt-1">Create professional EPUB ebooks for digital readers</p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="px-4 py-2 text-lg">
                {userCredits} Credits
              </Badge>
              <Button 
                onClick={() => {
                  resetWizard();
                  setShowNewDialog(true);
                }} 
                size="lg"
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-full shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Ebook
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mb-8 border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <BookOpen className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Professional EPUB Format</h3>
                  <p className="text-stone-600 text-sm">
                    Convert your completed books or external content into EPUB format, the universal standard for ebooks. 
                    Compatible with Kindle, Apple Books, Kobo, and all major e-readers.
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-emerald-700">
                    <span className="flex items-center gap-1">
                      <Smartphone className="h-4 w-4" />
                      Mobile Ready
                    </span>
                    <span className="flex items-center gap-1">
                      <Tablet className="h-4 w-4" />
                      E-Reader Compatible
                    </span>
                    <span className="flex items-center gap-1">
                      <ImageIcon className="h-4 w-4" />
                      Cover Art Support
                    </span>
                  </div>
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      💰 Cost: 2 Credits per ebook
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ebooks Grid */}
          {ebooks.length === 0 ? (
            <Card className="max-w-md mx-auto mt-12 border-stone-200 shadow-lg">
              <CardContent className="pt-12 pb-12 text-center">
                <BookOpen className="h-16 w-16 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold playfair text-stone-900 mb-2">No ebooks yet</h3>
                <p className="text-stone-600 mb-6">
                  Create your first ebook from a DTR book or external content
                </p>
                <Button 
                  onClick={() => {
                    resetWizard();
                    setShowNewDialog(true);
                  }}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Ebook
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ebooks.map((ebook) => (
                <Card key={ebook.id} className="border-stone-200 shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-2 bg-gradient-to-r from-emerald-600 to-emerald-400"></div>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg playfair text-stone-900 line-clamp-2">
                        {ebook.title}
                      </CardTitle>
                      <Badge 
                        className={
                          ebook.status === "completed" 
                            ? "bg-green-100 text-green-800" 
                            : ebook.status === "generating"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {ebook.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-sm text-stone-600">
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          EPUB
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {ebook.chapter_count} chapters
                        </span>
                      </div>
                      <div className="text-xs text-stone-500">
                        {formatFileSize(ebook.file_size)} • Created {new Date(ebook.created_at).toLocaleDateString()}
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        {ebook.status === "completed" && (
                          <Button 
                            onClick={() => handleDownload(ebook)}
                            className="flex-1 bg-emerald-700 hover:bg-emerald-600 text-white"
                            size="sm"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download EPUB
                          </Button>
                        )}
                        <Button 
                          onClick={() => handleDelete(ebook.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Ebook Dialog - Multi-step Wizard */}
      <Dialog open={showNewDialog} onOpenChange={(open) => {
        setShowNewDialog(open);
        if (!open) resetWizard();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair">Create New Ebook</DialogTitle>
            <DialogDescription>
              Step {wizardStep} of 3
            </DialogDescription>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  wizardStep >= step 
                    ? "bg-emerald-600 text-white" 
                    : "bg-stone-200 text-stone-500"
                }`}>
                  {wizardStep > step ? <Check className="h-4 w-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-1 ${
                    wizardStep > step ? "bg-emerald-600" : "bg-stone-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Step 1: Choose Source Type */}
          {wizardStep === 1 && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Choose Your Book Source</Label>
                <p className="text-sm text-stone-500 mt-1">Where is your book content coming from?</p>
              </div>
              
              <RadioGroup value={sourceType} onValueChange={setSourceType} className="space-y-3">
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  sourceType === "dtr_book" ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"
                }`} onClick={() => setSourceType("dtr_book")}>
                  <RadioGroupItem value="dtr_book" id="dtr_book" />
                  <div className="flex-1">
                    <Label htmlFor="dtr_book" className="text-base font-medium cursor-pointer">DTR Book</Label>
                    <p className="text-sm text-stone-500">Select from your completed books on the platform</p>
                  </div>
                  <BookOpen className="h-6 w-6 text-emerald-600" />
                </div>
                
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  sourceType === "external" ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"
                }`} onClick={() => setSourceType("external")}>
                  <RadioGroupItem value="external" id="external" />
                  <div className="flex-1">
                    <Label htmlFor="external" className="text-base font-medium cursor-pointer">External Book</Label>
                    <p className="text-sm text-stone-500">Paste or upload content from outside the platform</p>
                  </div>
                  <Upload className="h-6 w-6 text-emerald-600" />
                </div>
              </RadioGroup>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setWizardStep(2)}
                  disabled={!canProceedStep1}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select/Enter Book Content */}
          {wizardStep === 2 && (
            <div className="space-y-6 py-4">
              {sourceType === "dtr_book" ? (
                <>
                  <div>
                    <Label className="text-base font-semibold">Select Your Book</Label>
                    <p className="text-sm text-stone-500 mt-1">Choose a completed book from your dashboard</p>
                  </div>
                  
                  {books.length === 0 ? (
                    <div className="text-center py-8 bg-stone-50 rounded-lg">
                      <BookOpen className="h-12 w-12 text-stone-400 mx-auto mb-3" />
                      <p className="text-stone-600">No completed books found</p>
                      <p className="text-sm text-stone-500 mt-1">Complete a book first or use external content</p>
                    </div>
                  ) : (
                    <Select value={selectedBook} onValueChange={setSelectedBook}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Choose a completed book" />
                      </SelectTrigger>
                      <SelectContent>
                        {books.map((book) => (
                          <SelectItem key={book.id} value={book.id}>
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4" />
                              {book.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-base font-semibold">Upload or Enter Your Book</Label>
                    <p className="text-sm text-stone-500 mt-1">Upload a file or paste your book text</p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* File Upload Section */}
                    <div>
                      <Label>Upload Manuscript File</Label>
                      {uploadedFileName ? (
                        <div className="flex items-center justify-between p-3 mt-1 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-800">{uploadedFileName}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setUploadedFileName("");
                              setExternalContent("");
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-stone-300 rounded-lg p-4 mt-1 text-center hover:border-emerald-400 transition-colors">
                          <input
                            type="file"
                            accept=".txt,.docx,.pdf"
                            onChange={handleBookFileUpload}
                            className="hidden"
                            id="book-upload"
                            disabled={uploadingFile}
                          />
                          <label htmlFor="book-upload" className="cursor-pointer">
                            {uploadingFile ? (
                              <>
                                <Loader2 className="h-8 w-8 text-emerald-500 mx-auto mb-2 animate-spin" />
                                <p className="text-sm text-emerald-600">Extracting text...</p>
                              </>
                            ) : (
                              <>
                                <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                                <p className="text-sm text-stone-600">Click to upload manuscript</p>
                                <p className="text-xs text-stone-500 mt-1">.txt, .docx, or .pdf up to 20MB</p>
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-stone-300" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-stone-500">Or paste content</span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="title">Book Title *</Label>
                      <Input
                        id="title"
                        value={externalTitle}
                        onChange={(e) => setExternalTitle(e.target.value)}
                        placeholder="Enter the title of your book"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="content">Book Content *</Label>
                      <Textarea
                        id="content"
                        value={externalContent}
                        onChange={(e) => setExternalContent(e.target.value)}
                        placeholder="Paste your book content here... (chapters will be detected by 'Chapter' headings)"
                        className="mt-1 min-h-[150px]"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        {externalContent.length.toLocaleString()} characters
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setWizardStep(1)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={() => setWizardStep(3)}
                  disabled={!canProceedStep2}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Select Cover Art & Create */}
          {wizardStep === 3 && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Select Cover Art (Optional)</Label>
                <p className="text-sm text-stone-500 mt-1">Add a cover image to your ebook</p>
              </div>
              
              {/* Cover Source Selection */}
              <RadioGroup value={coverSourceType} onValueChange={(value) => {
                setCoverSourceType(value);
                if (value !== "dtr_cover") setSelectedCover("none");
                if (value !== "external") {
                  setUploadedCoverData(null);
                  setUploadedCoverName("");
                }
              }} className="space-y-3">
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "none" ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"
                }`} onClick={() => setCoverSourceType("none")}>
                  <RadioGroupItem value="none" id="cover_none" />
                  <div className="flex-1">
                    <Label htmlFor="cover_none" className="text-sm font-medium cursor-pointer">No Cover</Label>
                    <p className="text-xs text-stone-500">Continue without cover art</p>
                  </div>
                </div>
                
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "dtr_cover" ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"
                }`} onClick={() => setCoverSourceType("dtr_cover")}>
                  <RadioGroupItem value="dtr_cover" id="dtr_cover" />
                  <div className="flex-1">
                    <Label htmlFor="dtr_cover" className="text-sm font-medium cursor-pointer">DTR Cover</Label>
                    <p className="text-xs text-stone-500">Select from your created covers</p>
                  </div>
                  <ImageIcon className="h-5 w-5 text-emerald-600" />
                </div>
                
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "external" ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-emerald-300"
                }`} onClick={() => setCoverSourceType("external")}>
                  <RadioGroupItem value="external" id="cover_external" />
                  <div className="flex-1">
                    <Label htmlFor="cover_external" className="text-sm font-medium cursor-pointer">Upload Cover</Label>
                    <p className="text-xs text-stone-500">Upload your own cover image</p>
                  </div>
                  <Upload className="h-5 w-5 text-emerald-600" />
                </div>
              </RadioGroup>

              {/* DTR Cover Selection */}
              {coverSourceType === "dtr_cover" && (
                <div className="space-y-2">
                  {covers.length === 0 ? (
                    <div className="text-center py-6 bg-stone-50 rounded-lg">
                      <ImageIcon className="h-10 w-10 text-stone-400 mx-auto mb-2" />
                      <p className="text-sm text-stone-600">No covers available</p>
                      <p className="text-xs text-stone-500">Create covers in Cover Creation first</p>
                    </div>
                  ) : (
                    <Select value={selectedCover} onValueChange={setSelectedCover}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Choose a cover" />
                      </SelectTrigger>
                      <SelectContent>
                        {covers.map((cover) => (
                          <SelectItem key={cover.id} value={cover.id}>
                            <div className="flex items-center gap-2">
                              <ImageIcon className="h-4 w-4" />
                              {cover.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* External Cover Upload */}
              {coverSourceType === "external" && (
                <div className="space-y-2">
                  {uploadedCoverData ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">{uploadedCoverName}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setUploadedCoverData(null);
                          setUploadedCoverName("");
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverUpload}
                        className="hidden"
                        id="cover-upload"
                      />
                      <label htmlFor="cover-upload" className="cursor-pointer">
                        <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                        <p className="text-sm text-stone-600">Click to upload cover image</p>
                        <p className="text-xs text-stone-500 mt-1">PNG, JPG up to 5MB</p>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Summary & Credit Info */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-stone-900">Summary</h4>
                <div className="text-sm text-stone-600 space-y-1">
                  <div>Source: {sourceType === "dtr_book" ? "DTR Book" : "External Content"}</div>
                  <div>Book: {sourceType === "dtr_book" 
                    ? books.find(b => b.id === selectedBook)?.title || "Not selected"
                    : externalTitle || "Not entered"
                  }</div>
                  <div>Cover: {
                    coverSourceType === "none" ? "None" :
                    coverSourceType === "dtr_cover" ? (covers.find(c => c.id === selectedCover)?.title || "Not selected") :
                    uploadedCoverName || "Not uploaded"
                  }</div>
                </div>
                <div className="border-t border-emerald-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Cost:</span>
                    <span className="font-semibold text-emerald-700">2 Credits</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Your balance:</span>
                    <span className={userCredits >= 1 ? "text-green-600" : "text-red-600"}>
                      {userCredits} Credits
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setWizardStep(2)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleCreate}
                  disabled={creating || userCredits < 1}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Create Ebook
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EbookCreator;
